<script setup lang="ts">
import { computed, type Component } from 'vue'
import { RouterView, useRoute } from 'vue-router'

import LoadingOverlay from '@/components/common/LoadingOverlay.vue'
import NotificationHost from '@/components/common/NotificationHost.vue'
import { useDocumentTitle } from '@/composables/useDocumentTitle'
import AdminLayout from '@/layouts/AdminLayout.vue'
import AppLayout from '@/layouts/AppLayout.vue'
import AuthLayout from '@/layouts/AuthLayout.vue'
import GuestLayout from '@/layouts/GuestLayout.vue'
import { rendersNotFound } from '@/router/guards'
import { effectiveLayout } from '@/router/routes'
import { useAuthStore } from '@/stores/auth'
import { useUserStore } from '@/stores/user'
import NotFoundView from '@/views/NotFoundView.vue'

/**
 * Picks the shell from `route.meta.layout`. Chosen over nested layout routes so
 * that a page's URL stays independent from the chrome that wraps it.
 */
const route = useRoute()
const auth = useAuthStore()
const user = useUserStore()

const LAYOUTS: Record<'app' | 'auth' | 'guest' | 'admin', Component> = {
  app: AppLayout,
  auth: AuthLayout,
  guest: GuestLayout,
  admin: AdminLayout,
}

/**
 * Rotta del pannello aperta da un utente senza ruolo: si disegna il 404 al posto
 * della view, **tenendo l'URL**. Il redirect alla dashboard confermava che il
 * path esiste; così un utente qualunque vede esattamente quello che vedrebbe su
 * `/qualsiasi-cosa`.
 */
const notFound = computed(() =>
  rendersNotFound(route, {
    isAuthenticated: auth.isAuthenticated,
    isAdmin: user.isAdmin,
    isEditor: user.isEditor,
    // Non entrano nella decisione, ma il contesto è uno solo.
    isAllowedPath: () => false,
    maintenanceActive: false,
  }),
)

// Niente chrome del pannello attorno a un 404: chi non ci deve entrare non deve
// nemmeno vederne la sidebar.
const layout = computed<Component>(() =>
  notFound.value ? AppLayout : LAYOUTS[effectiveLayout(route.meta, auth.isAuthenticated)],
)

// Anche il titolo del tab deve dire "non trovata", non "Utenti".
useDocumentTitle(notFound)
</script>

<template>
  <component :is="layout">
    <!--
      Deliberately no <Transition mode="out-in"> around the router view.
      With `out-in`, the incoming page is only inserted after the outgoing one
      finishes leaving — so if that leave transition never completes, the user
      is left staring at an empty page until a reload. That is not theoretical:
      a leave transition stalls whenever requestAnimationFrame is starved, e.g.
      as soon as the document becomes hidden (background tab) mid-navigation.
      A 150ms fade is not worth a blank page, so the router view renders plainly.
    -->
    <NotFoundView v-if="notFound" />
    <RouterView v-else />
  </component>

  <!-- Outside the layout so toasts survive layout swaps. -->
  <NotificationHost />

  <!-- Sotto i toast (z-100), così un errore resta leggibile durante l'attesa. -->
  <LoadingOverlay />
</template>
